import axios from 'axios';
import AuthService from './AuthService';

const API = process.env.REACT_APP_API_URL;

// Helper function to get auth headers
async function getAuthHeaders() {
  const authService = new AuthService();
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error("No access token available");
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// Helper function for multipart form data headers
async function getMultipartAuthHeaders() {
  const authService = new AuthService();
  const token = await authService.getAccessToken();
  
  if (!token) {
    throw new Error("No access token available");
  }
  
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'multipart/form-data'
  };
}

// =================== ABCDE ===================
export interface ABCDEInput {
  annotationId: string;
  asymmetry: number;
  borders: number;
  color: number;
  diameter: number;
  evolution: number;
  notes: string;
  recommendation: string;
}

export interface ABCDEResult {
  asymmetry: number;
  borders: number;
  color: number;
  diameter: number;
  evolution: number;
  totalScore: number;
  notes: string;
  recommendation: string;
  createdAt?: string;
  updatedAt?: string;
  segmentationData?: {
    std_dev_r: number;
    std_dev_g: number;
    std_dev_b: number;
    circularity: number;
    asymmetry: number;
    asymmetry2: number;
    segmentedPlot?: string;
    assymetryPlot1?: string;
    assymetryplot2?: string;
    colorplot?: string;
    createdAt?: string;
    _additional: {
      id: string;
    };
  };
  annotationDetails?: {
    annotationId: string;
    label: string;
    x: number;
    y: number;
    width: number;
    height: number;
    confidence?: number;
    linkedMicroId?: string;
    linkedMicroUrl?: string;
    macroImageUrl?: string;
    createdAt?: string;
    _additional: {
      id: string;
    };
  };
  relatedImages?: Array<{
    annotationId: string;
    label: string;
    linkedMicroUrl?: string;
    macroImageUrl?: string;
    createdAt?: string;
    imageType: 'micro' | 'macro';
  }>;
  historicalAssessments?: Array<{
    annotationId: string;
    assessment: {
      asymmetry: number;
      borders: number;
      color: number;
      diameter: number;
      evolution: number;
      totalScore: number;
      notes: string;
      recommendation: string;
      createdAt?: string;
      _additional: {
        id: string;
      };
    };
    imageUrl?: string;
    createdAt?: string;
  }>;
  _additional: {
    id: string;
  };
}


export async function postABCDE(input: ABCDEInput): Promise<{ success: boolean; totalScore: number }> {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API}/abcde/annotation/abcde`, input, { headers });
  return response.data;
}

export async function getABCDE(annotationId: string): Promise<ABCDEResult[]> {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API}/abcde/annotation/abcde/${annotationId}`, { headers });
  if (!response.data) {
    return [];
  }
  return response.data;
}

export async function updateABCDE(assessmentId: string, input: ABCDEInput): Promise<{ success: boolean; totalScore: number; assessmentId: string }> {
  const headers = await getAuthHeaders();
  const response = await axios.put(`${API}/abcde/annotation/abcde/${assessmentId}`, input, { headers });
  return response.data;
}

// =================== Segmentation ===================
export interface SegmentationInput {
  annotationId: string;
  patientId: string;
  std_dev_r: number;
  std_dev_g: number;
  std_dev_b: number;
  circularity: number;
  asymmetry: number;
  asymmetry2: number;
  segmentedPlot: string;
  assymetryPlot1: string;
  assymetryplot2: string;
  colorplot: string;
  profileText: string;
}

export interface SegmentationResult {
  std_dev_r: number;
  std_dev_g: number;
  std_dev_b: number;
  colorplot: string;
  circularity: number;
  asymmetry: number;
  assymetryAverage: number;
  asymmetry2: number;
  segmentedPlot: string;
  assymetryPlot1: string;
  assymetryplot2: string;
  _additional: {
    id: string;
  };
}

export async function postSegmentation(input: SegmentationInput): Promise<{ success: boolean }> {
  const headers = await getAuthHeaders();
  const response = await axios.post(`${API}/abcde/annotation/segment`, input, { headers });
  return response.data;
}

export async function getSegmentation(annotationId: string): Promise<SegmentationResult[]> {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API}/abcde/annotation/segment/${annotationId}`, { headers });
  return response.data;
}

// =================== Segmentation Integration ===================
export async function runSegmentationAnalysis(annotationId: string, patientId: string, imagePath: string): Promise<{ success: boolean; summary: any }> {
  const headers = await getAuthHeaders();
  const segmentationInput = {
    annotationId,
    patientId,
    imagePath
  };
  const response = await axios.post(`${API}/abcde/annotation/segment`, segmentationInput, { headers });
  return response.data;
}

export async function getSegmentationForAnnotation(annotationId: string): Promise<SegmentationResult[]> {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API}/abcde/annotation/segment/${annotationId}`, { headers });
  if (!response.data) {
    return [];
  }
  return response.data;
}

export async function deleteSegmentation(segmentationId: string): Promise<{ success: boolean; message: string }> {
  const headers = await getAuthHeaders();
  const response = await axios.delete(`${API}/abcde/annotation/segment/${segmentationId}`, { headers });
  return response.data;
}

// =================== Annotation Details ===================
export interface AnnotationDetails {
  id: string;
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  confidence?: number;
  linkedMicroId?: string;
  linkedMicroUrl?: string;
  macroImageUrl?: string;
}

export async function getAnnotationDetails(annotationId: string): Promise<AnnotationDetails> {
  const headers = await getAuthHeaders();
  const response = await axios.get(`${API}/molemapping/annotation/${annotationId}`, { headers });
  return response.data;
}
